import _ from 'underscore';

import { ContentActionNotification } from '~commonInfra/contentActions/ContentActionNotification';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';

import { Formatters, Messages } from '../../codegen/Localize';
import { simpleActionErrorMsg, simpleActionSuccessMsg } from '../../commonInfra/contentActions/ActionHelpers';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { L10n } from '../../ts/L10n/L10n';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { FlowTasksPriorityDialogContent, FlowTasksPriorityDialogContentProps, FlowTasksPriorityDialogState } from '../components/FlowTasksPriorityDialogContent';
import { RunFlowTasks } from '../resources/RunFlowTasks';

export namespace ChangeRunFlowTasksPriorityAction {
  function setFlowTaskPriority(runFlowTaskInfos: Set<RunFlowTaskInfo>, priority: number) {
    return RunFlowTasks.setPriority(runFlowTaskInfos.keys(), priority);
  }
  function setLinkedTaskPriority(linkedTaskInfos: Set<RunFlowTaskInfo>, priority: number) {
    return LinkedTasks.setLinkedTasksPriority(linkedTaskInfos.pluck('linkedTaskId'), priority.toString());
  }

  export function dialog(runFlowTaskInfos: Set<RunFlowTaskInfo>) {
    const flowTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'FlowTask');
    const linkedTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'LinkedTask');
    let dialogMessage: string;
    if (runFlowTaskInfos.size() === 1) {
      const task = runFlowTaskInfos.first();
      if (linkedTasks.size() === 0) {
        dialogMessage = Messages.runFlowTasksSetPriorityPrompt({
          FLOW_NAME: L10n.elideIfLong(task.flow.name)
        });
      } else {
        dialogMessage = Messages.linkedTasksSetPriorityPrompt();
      }
    } else {
      dialogMessage = Messages.runFlowTasksSetPriorityMultiplePrompt({
        ITEM_COUNT_LOC: Formatters.formatCount(runFlowTaskInfos.size()),
      });
    }

    const priority = getPriority(runFlowTaskInfos);

    function priorityAction(priority: number): Promise<IResult> {
      if (flowTasks.size() > 0 && linkedTasks.size() > 0) {
        return setFlowTaskPriority(flowTasks, priority).then(() => {
          return setLinkedTaskPriority(linkedTasks, priority);
        });
      } else if (flowTasks.size() > 0) {
        return setFlowTaskPriority(flowTasks, priority);
      } else if (linkedTasks.size() > 0) {
        return setLinkedTaskPriority(linkedTasks, priority);
      }
    }
    const dialogInstance = openReactActionDialog<FlowTasksPriorityDialogContentProps, FlowTasksPriorityDialogState, IResult>({
      title: Messages.tasksSetPriority(),
      bodyComponent: FlowTasksPriorityDialogContent,
      initialBodyProps: {
        dialogMessage,
        start: priority,
      },
      initialDialogState: {
        value: priority,
      },
      dialogWidth: TabDialogWidth.width375px,
      dismissDialogOnActionFailed: true,
      actionName: Messages.tasksSetPriorityConfirm({
        ITEM_COUNT: runFlowTaskInfos.size(),
        ITEM_COUNT_LOC: Formatters.formatCount(runFlowTaskInfos.size()),
      }),
      action: (_, state) => priorityAction(state.value)
    });

    dialogInstance.result.then((result: IResult) => {
      ContentActionNotification.displaySingleToastForResult({
        items: runFlowTaskInfos,
        errors: result.errors,
        successMsg: simpleActionSuccessMsg(Messages.runFlowTasksSetPrioritySuccess),
        errorMsg: simpleActionErrorMsg(Messages.runFlowTasksSetPriorityError),
      });
    });

    return Promise.resolve(dialogInstance.result);
  }

  function getPriority(runFlowTaskInfos: Set<RunFlowTaskInfo>) {
    return Math.min(..._.map(runFlowTaskInfos.toArray(), (tasks: RunFlowTaskInfo) => tasks.priority));
  }
}
